import type {ImageSnapshot} from '../entities/image';
import type {EntityDispatchResult} from './adapter';

export type ImageFetcher = (url: string) => Promise<Blob>;

export interface ImageEntityResolverOptions {
  fetcher?: ImageFetcher;
}

interface ImageSource {
  sourceId: string;
  url: string;
}

export class ImageEntityResolver {
  private sources = new Map<string, ImageSource>();
  private cache = new Map<string, string>();
  private fetcher: ImageFetcher;

  constructor(opts: ImageEntityResolverOptions = {}) {
    this.fetcher = opts.fetcher ?? defaultFetcher;
  }

  create(spec: {
    publicId: string;
    sourceId: string;
    url: string;
  }): ImageSnapshot {
    // Synthetic ids (img:brand:*, img:compliance:*) are stable while their
    // backing source can change — a re-create must drop the old bytes.
    const previous = this.sources.get(spec.publicId);
    if (previous?.sourceId !== spec.sourceId) {
      this.cache.delete(spec.publicId);
    }
    this.sources.set(spec.publicId, {
      sourceId: spec.sourceId,
      url: spec.url,
    });
    return {
      type: 'Image',
      publicId: spec.publicId,
      sourceId: spec.sourceId,
    };
  }

  has(id: string): boolean {
    return this.sources.has(id);
  }

  forget(id: string): void {
    this.sources.delete(id);
    this.cache.delete(id);
  }

  /**
   * `payload.sourceId` is the source the iframe rendered with, so a resolve
   * that raced a source swap is refused rather than answered with old bytes.
   */
  async resolve(
    id: string,
    payload: Record<string, unknown> = {},
  ): Promise<EntityDispatchResult> {
    const source = this.sources.get(id);
    if (source == null) {
      return {ok: false, error: `unknown image id: ${id}`};
    }
    const expectedSourceId = payload['sourceId'];
    if (
      typeof expectedSourceId === 'string' &&
      expectedSourceId !== source.sourceId
    ) {
      return {ok: false, error: 'image source changed while resolving'};
    }

    const cached = this.cache.get(id);
    if (cached != null) {
      return {ok: true, data: {dataUrl: cached}};
    }

    try {
      const blob = await this.fetcher(source.url);
      const dataUrl = await blobToDataUrl(blob);
      // A re-create may have swapped the backing source while this fetch was
      // in flight; caching those bytes would serve the old image under the
      // stable synthetic id.
      const current = this.sources.get(id);
      if (current?.sourceId !== source.sourceId) {
        return {ok: false, error: 'image source changed while resolving'};
      }
      this.cache.set(id, dataUrl);
      return {ok: true, data: {dataUrl}};
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return {ok: false, error: message};
    }
  }
}

async function defaultFetcher(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`image fetch failed: ${res.status}`);
  }
  return res.blob();
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new Error(reader.error?.message ?? 'image-read-failed'));
    reader.readAsDataURL(blob);
  });
}
